import { JSXElementConstructor, PropsWithChildren, ReactElement } from 'react';
import { useMedia } from 'react-use';
import { useTheme } from 'styled-components';

import { media } from '@savgroup-front-common/constants';

import {
  safeFormattedIntlString,
  SafeFormattedMessage,
} from '../../formatters';
import { CheckIcon } from '../../protons/icons';
import Tooltip from '../Tooltip/Tooltip';

import { getOrderedWizardSteps } from './Wizard.helpers';
import {
  $WizardHidingPart,
  $WizardIndex,
  $WizardItemList,
  $WizardList,
  $WizardTooltip,
} from './Wizard.styles';
import { WizardStep } from './Wizard.types';

interface WizardProps<Context, Step extends WizardStep<Context>> {
  config: Step[];
  context: Partial<Context>;
  activeStepName: string;
  CustomWizard?: JSXElementConstructor<{
    orderedStep: Step[];
    activeStepName: string;
  }>;
}

function Wizard<Context, Step extends WizardStep<Context>>({
  config,
  activeStepName,
  context,
  CustomWizard,
}: PropsWithChildren<WizardProps<Context, Step>>): ReactElement | null {
  const theme = useTheme();
  const isMobileView = useMedia(media.maxWidth.xs);

  const orderedStep = getOrderedWizardSteps<Context, Step>({
    config,
    context,
  });
  const isCompact = orderedStep.length > 3 || isMobileView;
  let isAfterActive = false;

  if (orderedStep.length === 0) {
    return null;
  }

  if (CustomWizard) {
    return (
      <CustomWizard orderedStep={orderedStep} activeStepName={activeStepName} />
    );
  }

  return (
    <$WizardList>
      {orderedStep.map((item, index) => {
        const isActive = activeStepName === item.name;

        isAfterActive = isActive ? true : isAfterActive;

        if (!item.message) {
          throw new Error(
            `[Wizard] Item provided without message: ${JSON.stringify(item)}`,
          );
        }
        if (!item.name) {
          throw new Error(
            `[Wizard] Item provided without name: ${JSON.stringify(item)}`,
          );
        }

        return (
          <$WizardItemList
            key={item.name}
            $isActive={isActive}
            $isCompact={isCompact}
            $isSucceeded={!isAfterActive}
          >
            <Tooltip content={safeFormattedIntlString(item.message)}>
              <$WizardTooltip
                $isCompact={isCompact}
                $isActive={isActive}
                $isSucceeded={!isAfterActive}
              >
                <$WizardIndex>{index + 1}</$WizardIndex>
                <CheckIcon color={theme.colors.white} size="16px" />
                <$WizardHidingPart
                  $isActive={isActive}
                  $isCompact={isCompact}
                  $isSucceeded={!isAfterActive}
                >
                  {SafeFormattedMessage(item.message)}
                </$WizardHidingPart>
              </$WizardTooltip>
            </Tooltip>
          </$WizardItemList>
        );
      })}
    </$WizardList>
  );
}

export default Wizard;
